import {
  ImageHistoryEntry,
  GeneratedImage,
  ImageGenerationSettings,
} from '../types/imageGeneration';

/**
 * Service for managing image generation history with backend/localStorage fallback
 */
export class ImageHistoryService {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly namespace: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    this.apiKey =
      localStorage.getItem('bearerToken') ||
      process.env.REACT_APP_API_KEY ||
      '';
    this.namespace = 'frontend';
  }

  /**
   * Save image generation to history (backend first, localStorage fallback)
   */
  async saveImageGeneration(
    prompt: string,
    images: GeneratedImage[],
    settings: ImageGenerationSettings
  ): Promise<ImageHistoryEntry> {
    const entry: ImageHistoryEntry = {
      id: this.generateHistoryId(),
      prompt,
      images,
      timestamp: new Date(),
      settings,
    };

    // Try backend first
    try {
      await this.saveToServer(entry);
      return entry;
    } catch (error) {
      console.warn('Falling back to local image history save:', error);
      try {
        this.saveToLocalStorage(entry);
        return entry;
      } catch (e) {
        console.error('Failed to save image history locally:', e);
        throw e;
      }
    }
  }

  /**
   * Load image generation history (backend first, localStorage fallback)
   */
  async loadImageHistory(): Promise<ImageHistoryEntry[]> {
    // Prefer server results even if empty; only fallback to local on error
    try {
      const serverHistory = await this.loadFromServer();
      // Sync local cache with server truth to avoid stale entries
      this.replaceLocalStorage(serverHistory);
      return serverHistory;
    } catch (error) {
      console.warn('Failed to load server image history, using local cache:', error);
      try {
        return this.loadFromLocalStorage();
      } catch (e) {
        console.error('Failed to load image history locally:', e);
        return [];
      }
    }
  }

  /**
   * Get a specific history entry by ID
   */
  async getHistoryEntry(id: string): Promise<ImageHistoryEntry | null> {
    const history = await this.loadImageHistory();
    return history.find(entry => entry.id === id) || null;
  }

  /**
   * Delete a history entry
   */
  async deleteHistoryEntry(id: string): Promise<void> {
    // Try server deletion
    try {
      await this.deleteFromServer(id);
    } catch (error) {
      console.warn('Failed to delete from server:', error);
    }

    // Always try local cleanup
    this.deleteFromLocalStorage(id);
  }

  /**
   * Clear all history
   */
  async clearHistory(): Promise<void> {
    // Try server clear
    try {
      await this.clearServerHistory();
    } catch (error) {
      console.warn('Failed to clear server history:', error);
    }

    // Always clear local
    this.clearLocalStorage();
  }

  /**
   * Generate a unique history entry ID
   */
  private generateHistoryId(): string {
    return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ===== Local Storage Methods =====

  private saveToLocalStorage(entry: ImageHistoryEntry): void {
    const key = `image_history_${this.namespace}`;
    const existing = this.loadFromLocalStorage();
    const updated = [...existing, entry].slice(-100); 

    const data = {
      entries: updated,
      lastUpdated: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(data));
  }

  private loadFromLocalStorage(): ImageHistoryEntry[] {
    const key = `image_history_${this.namespace}`;
    const data = localStorage.getItem(key);

    if (!data) return [];

    try {
      const parsed = JSON.parse(data);
      return (parsed.entries || []).map((entry: any) => ({
        ...entry,
        timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
      }));
    } catch (e) {
      console.error('Failed to parse local image history:', e);
      return [];
    }
  }

  private deleteFromLocalStorage(id: string): void {
    const key = `image_history_${this.namespace}`;
    const existing = this.loadFromLocalStorage();
    const filtered = existing.filter(entry => entry.id !== id);

    const data = {
      entries: filtered,
      lastUpdated: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(data));
  }

  private clearLocalStorage(): void {
    const key = `image_history_${this.namespace}`;
    localStorage.removeItem(key);
  }

  private replaceLocalStorage(entries: ImageHistoryEntry[]): void {
    const key = `image_history_${this.namespace}`;
    const data = {
      entries,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(data));
  }

  // ===== Server Methods =====

  private async saveToServer(entry: ImageHistoryEntry): Promise<void> {
    const url = `${this.baseUrl}/store/history/${encodeURIComponent(this.namespace)}/${encodeURIComponent(entry.id)}`;
    const payload = {
      messages: this.imageEntryToMessages(entry),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok && response.status !== 204) {
      const text = await response.text().catch(() => '');
      throw new Error(
        `Failed to save image history: ${response.status} ${text}`
      );
    }
  }

  private async loadFromServer(): Promise<ImageHistoryEntry[]> {
    // Fetch list of chats for this namespace, then load only image history chats (id starts with 'img_')
    const listUrl = `${this.baseUrl}/store/chats/${encodeURIComponent(this.namespace)}`;
    const listRes = await fetch(listUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
    });
    if (!listRes.ok) {
      const text = await listRes.text().catch(() => '');
      throw new Error(`Failed to get server chat list: ${listRes.status} ${text}`);
    }
    const chatIds: string[] = await listRes.json();
    const imageIds = (Array.isArray(chatIds) ? chatIds : []).filter(id => typeof id === 'string' && id.startsWith('img_'));

    const entries: ImageHistoryEntry[] = [];

    for (const chatId of imageIds) {
      try {
        const histUrl = `${this.baseUrl}/store/history/${encodeURIComponent(this.namespace)}/${encodeURIComponent(chatId)}`;
        const res = await fetch(histUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
          },
          credentials: 'include',
        });
        if (!res.ok) continue;
        const records = await res.json();
        if (!Array.isArray(records)) continue;

        const entry = this.recordsToImageEntry(chatId, records);
        if (entry && entry.images && entry.images.length > 0) {
          entries.push(entry);
        }
      } catch (_) {
        // Skip problematic chat
      }
    }

    // Sort ascending by timestamp so newest appear at bottom in UI
    return entries.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }

  private async deleteFromServer(id: string): Promise<void> {
    const url = `${this.baseUrl}/store/history/${encodeURIComponent(this.namespace)}/${encodeURIComponent(id)}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
    });

    if (!response.ok && response.status !== 204) {
      const text = await response.text().catch(() => '');
      throw new Error(
        `Failed to delete image history: ${response.status} ${text}`
      );
    }
  }

  private async clearServerHistory(): Promise<void> {
    // For now, we can't easily clear all image history from server
    // since we'd need to know all the chat IDs. This is a limitation
    // of using the same endpoint as chat history.
    console.warn('Cannot clear server image history - would need to list all chat IDs');
  }

  // ===== Data Transformation Methods =====

  private imageEntryToMessages(entry: ImageHistoryEntry) {
    const messages = [];

    // Store generation settings in a system/meta message so we can reconstruct
    // accurate model + params when loading from server history.
    // This is not shown in the UI, but preserved in the store.
    messages.push({
      id: `${entry.id}_meta`,
      created_at: entry.timestamp.toISOString(),
      updated_at: entry.timestamp.toISOString(),
      message: {
        role: 'system',
        content: `__swarmind_image_meta__:${JSON.stringify(entry.settings)}`,
        parts: [
          {
            type: 'text',
            text: `__swarmind_image_meta__:${JSON.stringify(entry.settings)}`,
          },
        ],
      },
    });
    
    // Add user message with prompt
    messages.push({
      id: `${entry.id}_user`,
      created_at: entry.timestamp.toISOString(),
      updated_at: entry.timestamp.toISOString(),
      message: {
        role: 'user',
        content: entry.prompt,
        parts: [{ type: 'text', text: entry.prompt }],
      },
    });

    // Add assistant message with image URLs
    const imageUrls = entry.images.map(img => img.url).join('\n');
    messages.push({
      id: `${entry.id}_assistant`,
      created_at: entry.timestamp.toISOString(),
      updated_at: entry.timestamp.toISOString(),
      message: {
        role: 'assistant',
        content: imageUrls,
        parts: [{ type: 'text', text: imageUrls }],
      },
    });

    return messages;
  }

  private recordsToImageEntry(id: string, records: any[]): ImageHistoryEntry | null {
    // Find the user prompt and assistant image URLs
    let prompt = '';
    let imageUrlsText = '';
    let createdAt: Date | null = null;
    let parsedSettings: Partial<ImageGenerationSettings> | null = null;

    for (const rec of records) {
      const created = rec?.created_at ? new Date(rec.created_at) : null;
      if (!createdAt || (created && created < createdAt)) createdAt = created;

      const role = rec?.message?.role;
      const msg = rec?.message || {};
      const content: string = msg.content || msg.text || (Array.isArray(msg.parts) ? (msg.parts.map((p: any) => (p?.type === 'text' ? (p.text || '') : '')).join('')) : '');

      if (role === 'system' && content && content.startsWith('__swarmind_image_meta__:')) {
        const raw = content.slice('__swarmind_image_meta__:'.length);
        try {
          const meta = JSON.parse(raw);
          if (meta && typeof meta === 'object') {
            parsedSettings = meta as Partial<ImageGenerationSettings>;
          }
        } catch (_) {
          // ignore malformed meta
        }
      }

      if (role === 'user' && content) {
        prompt = content;
      } else if (role === 'assistant' && content) {
        imageUrlsText = content;
      }
    }

    const urls = imageUrlsText
      .split(/\s+/)
      .map(u => u.trim())
      .filter(u => u.startsWith('http://') || u.startsWith('https://'));

    const images = urls.map((url, index) => ({ url, index }));

    if (images.length === 0) return null;

    const settings: ImageGenerationSettings = {
      selectedModelId:
        (parsedSettings?.selectedModelId as string) || 'flux.1-dev',
      imageSize:
        (parsedSettings?.imageSize as ImageGenerationSettings['imageSize']) ||
        ('512x512' as const),
      steps: typeof parsedSettings?.steps === 'number' ? parsedSettings.steps : 20,
      quantity:
        typeof parsedSettings?.quantity === 'number'
          ? parsedSettings.quantity
          : images.length,
      seed:
        (parsedSettings?.seed as ImageGenerationSettings['seed']) ||
        ('random' as const),
    };

    return {
      id,
      prompt: prompt || 'Image generation',
      images,
      timestamp: createdAt || new Date(),
      settings,
    };
  }

}

export const imageHistoryService = new ImageHistoryService();
